import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import CatalogueView from "@/pages/admin/CatalogueView";
import { usesOrbitRoles, orbitRole, ORBIT_ROLES, ORBIT_LEVELS, LEVEL_STYLE } from "@/lib/roles";
import { roleHint } from "@/components/RoleSelect";
import { toast } from "sonner";
import AdminFormBuilder from "./AdminFormBuilder";
import { DataHealthView, FlashMissionsView } from "./AdminModules";
import AdminMaintenance from "./AdminMaintenance";
import RhSeuilsSettings from "./admin/RhSeuilsSettings";
import AdminOfficeManager from "./AdminOfficeManager";
import AdminWasaScore from "./AdminWasaScore";
import AdminIntranet from "./AdminIntranet";
import AdminBranding from "./admin/AdminBranding";
import AdminAutomation from "./admin/AdminAutomation";
import FormsAdmin from "./admin/forms/FormsAdmin";
import AdminDocuments from "./AdminDocuments";
import ForecastModelAdmin from "./ca/forecast/ForecastModelAdmin";
import AnalysisAdmin from "./ca/analysis/AnalysisAdmin";
import ProduitsAdmin from "./produits/ProduitsAdmin";
import ComparateurAdmin from "./achats/ComparateurAdmin";
import {
  Store, Users, Upload, ListChecks, ScrollText, ChevronRight, ArrowLeft,
  Plus, X, Loader2, User as UserIcon, MapPin, Trash2, Search, Link2,
  RefreshCw, Check, Wand2, Database, Sparkles, Filter, Palette, Edit3,
  Building2, Shield, Activity, TrendingUp, AlertTriangle, FileSpreadsheet,
  Mail, Radar, Zap, Layers, PenSquare, Layers3, FileText, ChevronDown, PenTool,
  Crown, Briefcase, User, HeartPulse, Target, Download, Package, Wrench, Megaphone, Rocket, Boxes
, Star } from "lucide-react";
import { OrbitAI } from "@/components/OrbitAI";
import { ProduitsBlocks } from "@/components/admin/ProduitsBlocks";
import { QualityImportModels } from "@/components/admin/QualityImportModels";

import FilterMultiSelect, { SearchSelect } from "@/components/filters/FilterMultiSelect";

const roleLabel = { admin: "Administrateur", director: "Directeur régional", manager: "Business Manager", rt: "Formateur", mm: "Manager Multi-sites", corner: "Corner", support: "Support" };
const roleColor = { admin: "bg-rose-100 text-rose-700 border-rose-200", director: "bg-indigo-100 text-indigo-700 border-indigo-200", manager: "bg-blue-100 text-blue-700 border-blue-200", rt: "bg-purple-100 text-purple-700 border-purple-200", mm: "bg-emerald-100 text-emerald-700 border-emerald-200", corner: "bg-slate-100 text-slate-700 border-slate-200", support: "bg-amber-100 text-amber-700 border-amber-200" };

const logAction = async (action, entity_type, extras = {}) => {
  try { await api.post("/admin/audit-log", { action, entity_type, ...extras }); } catch { /* silent */ }
};

// ═══════════════════════════════════════════════════════════════════════════
//  ROOT
// ═══════════════════════════════════════════════════════════════════════════
export default function Admin() {
  const [view, setView] = useState(() => new URLSearchParams(window.location.search).get("view") || "hub");
  const [users, setUsers] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [stores, setStores] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [u, s, st, hs] = await Promise.all([
        api.get("/users"),
        api.get("/sectors"),
        api.get("/stores"),
        api.get("/admin/hub-stats"),
      ]);
      setUsers(u.data); setSectors(s.data); setStores(st.data); setStats(hs.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Chargement impossible"); }
    setLoading(false);
  };
  useEffect(() => { loadAll(); }, []);

  const commonProps = { users, sectors, stores, reload: loadAll, setView };

  return (
    <div className="space-y-6" data-testid="admin-page">
      <AdminHeader view={view} setView={setView} stats={stats}/>
      {view === "hub" && <HubView stats={stats} loading={loading} setView={setView}/>}
      {view === "parc" && <ParcView {...commonProps}/>}
      {view === "teams" && <TeamsView {...commonProps}/>}
      {view === "imports" && <ImportsView {...commonProps}/>}
      {view === "forms" && <FormsView {...commonProps}/>}
      {view === "catalogue" && <CatalogueView/>}
      {view === "audit" && <AuditView {...commonProps}/>}
      {view === "health" && <DataHealthView/>}
      {view === "forecast_model" && <ForecastModelAdmin/>}
      {view === "analysis_ca" && <AnalysisAdmin/>}
      {view === "analysis_produits" && <ProduitsAdmin/>}
      {view === "comparateur_achats" && <ComparateurAdmin/>}
      {view === "workflows" && <AdminAutomation/>}
      {view === "form_builder" && <FormsAdmin/>}
      {view === "flash" && <FlashMissionsView sectors={sectors}/>}
      {view === "maintenance" && <AdminMaintenance/>}
      {view === "rh_seuils" && <RhSeuilsSettings/>}
      {view === "office_manager" && <AdminOfficeManager/>}
      {view === "wasascore" && <AdminWasaScore/>}
      {view === "intranet" && <AdminIntranet/>}
      {view === "branding" && <AdminBranding/>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  HEADER
// ═══════════════════════════════════════════════════════════════════════════
function AdminHeader({ view, setView, stats }) {
  const titles = {
    hub: { t: "Centre de contrôle", s: "Pilotage complet du réseau Orbit — magasins, équipes, imports, formulaires." },
    parc: { t: "Gestion du Parc", s: "Magasins & corners : ajout, réaffectation BM/DR, mapping." },
    teams: { t: "Équipes & Accès", s: "Utilisateurs, rôles et rattachement aux secteurs." },
    imports: { t: "Centre d'Import", s: "Excel, CSV et historique des imports." },
    forms: { t: "Configuration des Formulaires", s: "Listes déroulantes de toutes les pages, sans toucher au code." },
    audit: { t: "Journal d'activité", s: "Qui a modifié quoi, quand." },
    wasascore: { t: "WasaScore", s: "Pondération des indicateurs du WasaScore (CA LFL, foodcost, casse, hygiène, productivité)." },
    forecast_model: { t: "Modèle de projection CA", s: "Référentiels CA › paramètres du moteur Projection & Simulation." },
    analysis_ca: { t: "Analyse CA", s: "Seuils de rupture, volatilité, Z-score, sous-performance, impact important et score de priorité." },
    comparateur_achats: { t: "Comparateur Achats", s: "Tolérance de tranche CA, nombre minimum de comparables, benchmark médian, fournisseurs affichés." },
    analysis_produits: { t: "Analyse Produits", s: "Seuils de sur/sous-représentation, tendances, anomalies, longue queue et impact minimal des recommandations." },
    health: { t: "Santé des Données", s: "Scan des imports par magasin × semaine, relance en 1 clic." },
    workflows: { t: "Règles & Alertes automatiques", s: "Seuils KPI, escalade terrain et indicateurs métier : alertes + tickets d'action créés automatiquement." },
    form_builder: { t: "Éditeur de formulaires", s: "Créez, modifiez, publiez et gérez tous les formulaires d'Orbit (audits, visites, hygiène, inventaire, RH…) sans code." },
    flash: { t: "Missions Flash", s: "Missions ponctuelles Direction avec suivi photo en temps réel." },
    maintenance: { t: "Maintenance", s: "Types d'intervention, parc d'équipements, prestataires et plans préventifs." },
    intranet: { t: "Contenus Intranet", s: "Actualités, événements et communications importantes publiés sur l'Intranet." },
    catalogue: { t: "Catalogue produits", s: "Référentiel produits de votre espace : nom, code-barres, catégorie, unité, prix unitaire HT. Import Excel." },
  };
  const cur = titles[view] || titles.hub;
  return (
    <div className="flex items-center justify-between flex-wrap gap-3">
      <div className="flex items-center gap-3">
        {view !== "hub" && (
          <button onClick={() => setView("hub")} data-testid="admin-back"
                  className="w-10 h-10 rounded-xl bg-white border border-slate-200 hover:bg-slate-50 grid place-items-center text-slate-600">
            <ArrowLeft className="w-4 h-4"/>
          </button>
        )}
        <div>
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-blue-600">
            <Shield className="w-3.5 h-3.5"/> Administration
          </div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">{cur.t}</h1>
          <p className="text-slate-500 mt-1 text-sm">{cur.s}</p>
        </div>
      </div>
      {view === "hub" && stats && (
        <div className="hidden md:flex items-center gap-2 bg-white border border-slate-200 rounded-2xl px-4 py-2.5">
          <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"/>
          <span className="text-xs font-semibold text-slate-500">{stats.audit?.last_24h || 0} actions dans les dernières 24h</span>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  HUB — home cards + KPI strip + recent audit
// ═══════════════════════════════════════════════════════════════════════════
function HubView({ stats, loading, setView }) {
  if (loading || !stats) return <div className="py-20 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin inline"/></div>;

  const kpiTiles = [
    { icon: Building2, label: "Magasins actifs", value: stats.stores.total, sub: `${stats.stores.geo_ok}/${stats.stores.total} géolocalisés`, color: "from-blue-500 to-blue-600" },
    { icon: Users, label: "Utilisateurs", value: stats.users.total, sub: `${stats.users.active_week} actifs cette semaine`, color: "from-indigo-500 to-purple-600" },
    { icon: FileSpreadsheet, label: "Imports · 7j", value: stats.imports.week, sub: `${stats.imports.success_week} succès`, color: "from-emerald-500 to-teal-600" },
    { icon: AlertTriangle, label: "Corners orphelins", value: stats.mapping.orphans, sub: "à mapper vers un magasin", color: "from-amber-500 to-orange-600" },
  ];

  const blocks = [
    { key: "branding", icon: Palette, title: "Personnalisation", desc: "Logo de l'entreprise et couleur principale appliqués à toute l'interface (marque blanche).", stat: "Marque blanche", accent: "from-violet-50 to-fuchsia-50", iconBg: "bg-violet-600", border: "border-violet-100 hover:border-violet-300" },
    { key: "parc", icon: Store, title: "Gestion du Parc", desc: "Ajouter · modifier · supprimer un corner. Réaffectation cascade Région > Secteur > DR > BM.", stat: `${stats.stores.total} magasins`, accent: "from-blue-50 to-cyan-50", iconBg: "bg-blue-500", border: "border-blue-100 hover:border-blue-300" },
    { key: "teams", icon: Users, title: "Équipes & Accès", desc: "N1 · Direction · N2 · Itinérant · N3 · Corner. Sous-rôles éditables.", stat: `${stats.users.total} utilisateurs`, accent: "from-indigo-50 to-purple-50", iconBg: "bg-indigo-500", border: "border-indigo-100 hover:border-indigo-300" },
    { key: "integrations", icon: Upload, href: "/admin/integrations/inpulse", title: "Intégrations", desc: "Inpulse : connexion sécurisée, référentiel réseau, mapping des points de vente.", stat: "Inpulse API v2", accent: "from-blue-50 to-indigo-50" },
    { key: "imports", icon: Upload, title: "Centre d'Import", desc: "6 slots métier (CA, Qualité, RH, P&L, Achats, Productivité) + historique.", stat: `${stats.imports.week} cette semaine`, accent: "from-emerald-50 to-teal-50", iconBg: "bg-emerald-500", border: "border-emerald-100 hover:border-emerald-300" },
    { key: "catalogue", icon: Package, title: "Catalogue produits", desc: "Références produits (nom, code-barres, catégorie, unité, prix HT). Import Excel. Base de l'inventaire et du scanner.", stat: "Référentiel", accent: "from-cyan-50 to-sky-50", iconBg: "bg-cyan-600", border: "border-cyan-100 hover:border-cyan-300" },
    { key: "forms", icon: ListChecks, title: "Configuration Formulaires", desc: "Listes déroulantes + Builder no-code drag-and-drop.", stat: "5 formulaires actifs", accent: "from-amber-50 to-orange-50", iconBg: "bg-amber-500", border: "border-amber-100 hover:border-amber-300" },
    { key: "health", icon: HeartPulse, title: "Santé des Données", desc: "Grille magasin × semaine, données manquantes en rouge, relance en 1 clic.", stat: "Scan en temps réel", accent: "from-rose-50 to-pink-50", iconBg: "bg-rose-500", border: "border-rose-100 hover:border-rose-300" },
    { key: "forecast_model", icon: TrendingUp, title: "Référentiels CA · Modèle de projection", desc: "Poids N-1 / tendances / saisonnalité / calendrier, pondération des semaines, méthode N-1, seuils de l'indice de cohérence, règles fériés & impacts extrêmes.", stat: "Projection & Simulation", accent: "from-blue-50 to-violet-50", iconBg: "bg-blue-600", border: "border-blue-100 hover:border-blue-300" },
    { key: "analysis_ca", icon: Activity, title: "Analyse CA · Seuils & signaux", desc: "Rupture, volatilité, Z-score, sous-performance persistante, impact important, observations minimum, poids du score de priorité.", stat: "Moteur d'analyse", accent: "from-sky-50 to-blue-50", iconBg: "bg-sky-600", border: "border-sky-100 hover:border-sky-300" },
    { key: "analysis_produits", icon: Boxes, title: "Analyse Produits · Seuils", desc: "Sur/sous-représentation vs référence, fenêtres tendance & anomalie, z-score, longue queue, impact minimal des recommandations.", stat: "Moteur produit", accent: "from-indigo-50 to-blue-50", iconBg: "bg-indigo-600", border: "border-indigo-100 hover:border-indigo-300" },
    { key: "comparateur_achats", icon: Target, title: "Comparateur Achats · Seuils", desc: "Tolérance de tranche CA par défaut et options, nombre minimum de comparables, benchmark médian, fournisseurs affichés.", stat: "Achats", accent: "from-orange-50 to-amber-50", iconBg: "bg-orange-500", border: "border-orange-100 hover:border-orange-300" },
    { key: "form_builder", icon: PenTool, title: "Éditeur de formulaires", desc: "Drag & drop, logique conditionnelle, scores, déclencheurs, workflow de validation, génération IA. Utilisable dans tous les modules.", stat: "Sans code", accent: "from-orange-50 to-amber-50", iconBg: "bg-orange-500", border: "border-orange-200 hover:border-orange-400" },
    { key: "workflows", icon: Radar, title: "Règles & Alertes automatiques", desc: "Seuils KPI (CA, food cost, casse, RH…), escalade des tickets sans réponse, hygiène & inventaire → alertes + tickets d'action.", stat: "Moteur actif · horaire", accent: "from-slate-50 to-slate-100", iconBg: "bg-slate-700", border: "border-slate-200 hover:border-slate-400" },
    { key: "flash", icon: Zap, title: "Missions Flash", desc: "Direction N1 → toutes / région / secteur. Photo obligatoire, suivi live.", stat: "Direction only", accent: "from-purple-50 to-pink-50", iconBg: "bg-gradient-to-br from-purple-600 to-pink-600", border: "border-purple-100 hover:border-purple-300" },
    { key: "office_manager", icon: Building2, title: "Office Manager", desc: "Flotte véhicules · Matériel & équipements · Affectations · Fournisseurs (Moyens Généraux).", stat: "3 sections", accent: "from-sky-50 to-blue-50", iconBg: "bg-sky-500", border: "border-sky-100 hover:border-sky-300" },
    { key: "rh_seuils", icon: Wrench, title: "Seuils RH", desc: "Règles de priorisation du Cockpit RH : recrutement ancien, intégration en retard, arrêts non traités, HS récurrentes.", stat: "7 seuils", accent: "from-violet-50 to-indigo-50", iconBg: "bg-violet-600", border: "border-violet-100 hover:border-violet-300" },
    { key: "wasascore", icon: Star, title: "WasaScore", desc: "Poids configurables des 5 indicateurs · classement mensuel des corners Wasabi.", stat: "5 indicateurs" },
    { key: "maintenance", icon: Wrench, title: "Maintenance", desc: "Types d'intervention · Équipements · Prestataires · Plans préventifs (auto-génération d'interventions).", stat: "4 sections", accent: "from-orange-50 to-red-50", iconBg: "bg-orange-500", border: "border-orange-100 hover:border-orange-300" },
    { key: "campaigns", icon: Rocket, title: "Gestion Campagnes Réseau", desc: "Créer, planifier, déployer et mesurer des campagnes (commerciales, PLV, qualité, RH…) sur tout le réseau : checklists, formulaires, missions, carte, IA.", stat: "Déploiement réseau", accent: "from-orange-50 to-rose-50", iconBg: "bg-gradient-to-br from-orange-500 to-rose-500", border: "border-orange-200 hover:border-orange-400" },
    { key: "intranet", icon: Megaphone, title: "Contenus Intranet", desc: "Actualités · Événements · Communications importantes. Formulaires dédiés de publication.", stat: "3 types de contenu", accent: "from-blue-50 to-indigo-50", iconBg: "bg-blue-700", border: "border-blue-100 hover:border-blue-300" },
  ];

  return (
    <div className="space-y-6" data-testid="admin-hub">
      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {kpiTiles.map(k => (
          <div key={k.label} className="relative bg-white border border-slate-200 rounded-2xl p-4 overflow-hidden">
            <div className={`absolute -right-4 -top-4 w-24 h-24 rounded-full bg-gradient-to-br ${k.color} opacity-10`}/>
            <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${k.color} text-white grid place-items-center shadow-sm`}>
              <k.icon className="w-5 h-5"/>
            </div>
            <div className="mt-3 text-[10px] font-bold uppercase tracking-widest text-slate-400">{k.label}</div>
            <div className="font-display text-3xl font-bold text-slate-900 mt-0.5">{k.value}</div>
            <div className="text-xs text-slate-500 mt-1">{k.sub}</div>
          </div>
        ))}
      </div>

      {/* 4 main blocks */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {blocks.map(b => (
          <button key={b.key} onClick={() => b.key === "campaigns" ? (window.location.href = "/campaigns") : b.href ? (window.location.href = b.href) : setView(b.key)} data-testid={`admin-card-${b.key}`}
                  className={`group text-left bg-gradient-to-br ${b.accent} border-2 ${b.border} rounded-2xl p-6 transition-all hover:shadow-lg hover:-translate-y-0.5`}>
            <div className="flex items-start justify-between">
              <div className={`w-12 h-12 rounded-xl ${b.iconBg} text-white grid place-items-center shadow-md group-hover:scale-110 transition-transform`}>
                <b.icon className="w-6 h-6"/>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-slate-700 group-hover:translate-x-1 transition-all"/>
            </div>
            <h3 className="font-display text-xl font-bold text-slate-900 mt-4">{b.title}</h3>
            <p className="text-sm text-slate-600 mt-1 leading-relaxed">{b.desc}</p>
            <div className="mt-3 inline-block text-[11px] font-bold uppercase tracking-wider text-slate-500 bg-white/60 px-2.5 py-1 rounded-full">
              {b.stat}
            </div>
          </button>
        ))}
      </div>

      {/* Recent audit log widget */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <ScrollText className="w-4 h-4 text-slate-500"/>
            <h3 className="font-display font-bold text-slate-900">Dernières actions</h3>
          </div>
          <button onClick={() => setView("audit")} data-testid="hub-view-audit-full"
                  className="text-xs font-bold text-blue-600 hover:text-blue-800 flex items-center gap-1">
            Voir tout <ChevronRight className="w-3 h-3"/>
          </button>
        </div>
        {(stats.audit?.recent || []).length === 0 ? (
          <div className="text-center py-8 text-slate-400 text-sm italic">Aucune action enregistrée pour l'instant.</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {stats.audit.recent.map(a => (
              <li key={a.id} className="py-2.5 flex items-start gap-3 text-sm" data-testid={`hub-audit-${a.id}`}>
                <div className="w-8 h-8 rounded-lg bg-slate-100 grid place-items-center text-slate-500 shrink-0">
                  <Activity className="w-4 h-4"/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-slate-800"><b>{a.user_name}</b> {a.summary || a.action}</div>
                  <div className="text-[11px] text-slate-400 mt-0.5">{new Date(a.created_at).toLocaleString("fr-FR")}</div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  PARC — stores management + mapping
// ═══════════════════════════════════════════════════════════════════════════
function ParcView({ users, sectors, stores, reload }) {
  const [tab, setTab] = useState("stores");
  const [search, setSearch] = useState("");
  const [reassignStore, setReassignStore] = useState(null);
  const [cascade, setCascade] = useState({ region: "", sector_id: "", dr_id: "", bm_id: "" });
  const [modal, setModal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [stForm, setStForm] = useState({ name: "", sector_id: "", address: "" });
  const [geoStore, setGeoStore] = useState(null);
  const [geoLat, setGeoLat] = useState("");
  const [geoLng, setGeoLng] = useState("");

  const drs = users.filter(u => u.role === "director");
  const bms = users.filter(u => u.role === "manager");
  const regions = useMemo(() => Array.from(new Set(sectors.map(s => s.region).filter(Boolean))).sort(), [sectors]);
  const sectorsInRegion = useMemo(() => cascade.region ? sectors.filter(s => s.region === cascade.region) : sectors, [sectors, cascade.region]);
  const drsInRegion = useMemo(() => {
    if (!cascade.region) return drs;
    const regionSectorIds = sectorsInRegion.map(s => s.id);
    const drIds = new Set(bms.filter(bm => (bm.sectors || []).some(sid => regionSectorIds.includes(sid))).map(bm => bm.dr_id).filter(Boolean));
    return drs.filter(d => drIds.has(d.id));
  }, [drs, bms, cascade.region, sectorsInRegion]);
  const bmsInFilter = useMemo(() => {
    let list = bms;
    if (cascade.dr_id) list = list.filter(bm => bm.dr_id === cascade.dr_id);
    if (cascade.sector_id) list = list.filter(bm => (bm.sectors || []).includes(cascade.sector_id));
    return list;
  }, [bms, cascade]);

  const createStore = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const { data } = await api.post("/stores", stForm);
      await logAction("store.create", "store", { entity_id: data.id, entity_label: data.name, summary: `a créé le magasin ${data.name}`, after: data });
      toast.success("Magasin créé"); setModal(null); setStForm({ name: "", sector_id: "", address: "" }); reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const deleteStore = async (s) => {
    if (!confirm(`Supprimer « ${s.name} » ?`)) return;
    try {
      await api.delete(`/stores/${s.id}`);
      await logAction("store.delete", "store", { entity_id: s.id, entity_label: s.name, summary: `a supprimé le magasin ${s.name}`, before: s });
      toast.success("Supprimé"); reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Suppression impossible"); }
  };

  const submitReassign = async (e) => {
    e.preventDefault();
    if (!cascade.bm_id) return toast.error("Sélectionnez un BM");
    try {
      const { data } = await api.patch(`/stores/${reassignStore.id}/assign`, { bm_id: cascade.bm_id });
      await logAction("store.reassign", "store", {
        entity_id: reassignStore.id,
        entity_label: reassignStore.name,
        summary: `a réaffecté ${reassignStore.name} à ${data.bm_name} (DR: ${data.dr_name || "—"})`,
        before: { bm_name: reassignStore.bm_name, dr_name: reassignStore.dr_name },
        after: { bm_name: data.bm_name, dr_name: data.dr_name },
      });
      toast.success(`Réaffecté à ${data.bm_name} (DR: ${data.dr_name || "—"})`);
      setReassignStore(null); setCascade({ region: "", sector_id: "", dr_id: "", bm_id: "" }); reload();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const submitGeo = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/stores/${geoStore.id}/geo`, { lat: parseFloat(geoLat), lng: parseFloat(geoLng) });
      await logAction("store.geo", "store", { entity_id: geoStore.id, entity_label: geoStore.name, summary: `a défini les coordonnées GPS de ${geoStore.name}` });
      toast.success(`Coordonnées enregistrées pour ${geoStore.name}`);
      setGeoStore(null); reload();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const q = search.trim().toLowerCase();
  const filteredStores = stores.filter(s => !q
    || (s.name || "").toLowerCase().includes(q)
    || (s.address || "").toLowerCase().includes(q)
    || (s.bm_name || "").toLowerCase().includes(q)
    || (s.dr_name || "").toLowerCase().includes(q));

  return (
    <div className="space-y-4" data-testid="parc-view">
      <div className="flex items-center gap-2 border-b border-slate-200">
        {[["stores", "Magasins & Corners", Store, filteredStores.length], ["mapping", "Mapping Corners", Link2, null]].map(([k, l, I, count]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`parc-tab-${k}`}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 transition-colors ${tab === k ? "border-blue-600 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            <I className="w-4 h-4"/> {l}{count !== null && <span className="text-[10px] font-bold text-slate-400">({count})</span>}
          </button>
        ))}
        {tab === "stores" && (
          <button onClick={() => setModal("store")} data-testid="parc-add-store"
                  className="ml-auto flex items-center gap-2 px-3 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm">
            <Plus className="w-4 h-4"/> Ajouter
          </button>
        )}
      </div>

      {tab === "stores" && (
        <>
          <SearchBar value={search} onChange={setSearch} placeholder="Rechercher un magasin, une ville, un BM…"/>
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-xs font-semibold tracking-wider text-slate-500 uppercase">
                  <th className="px-4 py-3">Magasin</th>
                  <th className="px-4 py-3">BM affecté</th>
                  <th className="px-4 py-3">DR</th>
                  <th className="px-4 py-3">Géo</th>
                  <th className="px-4 py-3 w-40 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredStores.map(s => {
                  const hasGeo = s.lat && s.lng;
                  return (
                    <tr key={s.id} data-testid={`parc-store-${s.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900">{s.name}</div>
                        <div className="text-xs text-slate-400 truncate max-w-xs">{s.address || "—"}</div>
                      </td>
                      <td className="px-4 py-3 text-slate-700 text-sm">{s.bm_name || s.responsible_name || "—"}</td>
                      <td className="px-4 py-3 text-slate-700 text-sm">{s.dr_name || "—"}</td>
                      <td className="px-4 py-3">
                        {hasGeo
                          ? <span className="inline-flex items-center gap-1 text-emerald-600 text-xs font-semibold"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500"/>OK</span>
                          : <span className="inline-flex items-center gap-1 text-rose-600 text-xs font-semibold"><span className="w-1.5 h-1.5 rounded-full bg-rose-500"/>Manquante</span>}
                      </td>
                      <td className="px-4 py-3 text-right space-x-1">
                        <button onClick={() => { setReassignStore(s); setCascade({ region: s.dr_name ? (sectors.find(x => x.id === s.sector_id)?.region || "") : "", sector_id: s.sector_id || "", dr_id: s.dr_id || "", bm_id: s.bm_id || "" }); }} data-testid={`parc-reassign-${s.id}`}
                                className="text-xs px-2.5 py-1 rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100 font-semibold">Réaffecter</button>
                        {!hasGeo && (
                          <button onClick={() => { setGeoStore(s); setGeoLat(""); setGeoLng(""); }} data-testid={`parc-geo-${s.id}`}
                                  className="text-xs px-2.5 py-1 rounded-lg bg-amber-50 text-amber-700 hover:bg-amber-100 font-semibold">Géo</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {filteredStores.length === 0 && (
                  <tr><td colSpan={5} className="text-center py-10 text-slate-400 italic text-sm">Aucun magasin trouvé.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === "mapping" && <CornerMappingTab stores={stores}/>}

      {/* Reassign modal with cascade Région > Secteur > DR > BM */}
      {reassignStore && (
        <Modal onClose={() => setReassignStore(null)} title="Réaffecter le magasin">
          <form onSubmit={submitReassign} className="p-6 space-y-4">
            <div>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Magasin</div>
              <div className="font-semibold text-slate-900 mt-0.5">{reassignStore.name}</div>
              <div className="text-xs text-slate-500 mt-0.5">Actuellement : <b>{reassignStore.bm_name || "—"}</b> (DR: {reassignStore.dr_name || "—"})</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">1. Région</label>
                <SearchSelect value={cascade.region} onChange={v => setCascade({ region: v, sector_id: "", dr_id: "", bm_id: "" })} options={[...regions.map(r => ({ value: r, label: r }))]} placeholder={"— Toutes régions —"} data-testid="cascade-region" size="lg" menuWidth={260}/>
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">2. Secteur</label>
                <SearchSelect value={cascade.sector_id} onChange={v => setCascade(c => ({ ...c, sector_id: v, bm_id: "" }))} options={[...sectorsInRegion.map(s => ({ value: s.id, label: s.name }))]} placeholder={"— Tous secteurs —"} data-testid="cascade-sector" size="lg" menuWidth={260}/>
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">3. Directeur Régional</label>
                <SearchSelect value={cascade.dr_id} onChange={v => setCascade(c => ({ ...c, dr_id: v, bm_id: "" }))} options={[...drsInRegion.map(d => ({ value: d.id, label: d.name }))]} placeholder={"— Tous DR —"} data-testid="cascade-dr" size="lg" menuWidth={260}/>
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">4. Business Manager <span className="text-rose-500">*</span></label>
                <SearchSelect value={cascade.bm_id} onChange={v => setCascade(c => ({ ...c, bm_id: v }))} testid="cascade-bm" className="mt-1" size="lg" menuWidth={280}
                              placeholder="— Sélectionnez —"
                              options={bmsInFilter.map(bm => { const dr = users.find(x => x.id === bm.dr_id); return { value: bm.id, label: `${bm.name}${dr ? ` — DR: ${dr.name}` : ""}` }; })}/>
              </div>
            </div>
            <div className="text-[11px] text-slate-500">Cascade : chaque niveau filtre le suivant. Le DR est déduit du BM sélectionné.</div>
            <ModalFooter onCancel={() => setReassignStore(null)} submitLabel="Réaffecter" testid="parc-reassign-submit"/>
          </form>
        </Modal>
      )}

      {/* Geo modal */}
      {geoStore && (
        <Modal onClose={() => setGeoStore(null)} title="Coordonnées GPS">
          <form onSubmit={submitGeo} className="p-6 space-y-4">
            <div>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Magasin</div>
              <div className="font-semibold text-slate-900 mt-0.5">{geoStore.name}</div>
              <div className="text-xs text-slate-500 mt-0.5">{geoStore.address || "—"}</div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Latitude</label>
                <input required type="number" step="any" value={geoLat} onChange={e=>setGeoLat(e.target.value)}
                       className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 text-sm" placeholder="-21.1234"/>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Longitude</label>
                <input required type="number" step="any" value={geoLng} onChange={e=>setGeoLng(e.target.value)}
                       className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 text-sm" placeholder="55.5678"/>
              </div>
            </div>
            <ModalFooter onCancel={() => setGeoStore(null)} submitLabel="Enregistrer"/>
          </form>
        </Modal>
      )}

      {/* Create store modal */}
      {modal === "store" && (
        <Modal onClose={() => setModal(null)} title="Nouveau magasin">
          <form onSubmit={createStore}>
            <div className="p-6 space-y-4">
              <input required placeholder="Nom" value={stForm.name} onChange={e=>setStForm({...stForm, name: e.target.value})} data-testid="parc-new-store-name" className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm"/>
              <SearchSelect value={stForm.sector_id} onChange={v => setStForm({...stForm, sector_id: v})} options={[...sectors.map(s => ({ value: s.id, label: s.name }))]} placeholder={"— Secteur —"} required data-testid="parc-new-store-sector" size="lg" menuWidth={260}/>
              <input placeholder="Adresse" value={stForm.address} onChange={e=>setStForm({...stForm, address: e.target.value})} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm"/>
            </div>
            <ModalFooter onCancel={() => setModal(null)} submitLabel={saving ? "…" : "Créer"} disabled={saving} testid="parc-new-store-submit"/>
          </form>
        </Modal>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  TEAMS — users management
// ═══════════════════════════════════════════════════════════════════════════
// Role level mapping: main role → level
const ROLE_LEVELS = {
  admin: "N1", director: "N1",
  manager: "N2", rt: "N2", mm: "N2", support: "N2",
  corner: "N3",
};
const LEVEL_COLOR = {
  N1: "bg-gradient-to-r from-rose-500 to-red-600 text-white",
  N2: "bg-gradient-to-r from-blue-500 to-indigo-600 text-white",
  N3: "bg-gradient-to-r from-slate-500 to-slate-700 text-white",
};

function TeamsView({ users, sectors, stores = [], reload }) {
  const { user: me } = useAuth(); const orbitTenant = usesOrbitRoles(me?.tenant);
  const [search, setSearch] = useState("");
  const [levelFilter, setLevelFilter] = useState("all");
  const [modal, setModal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [subRoles, setSubRoles] = useState({ subrole_n1: [], subrole_n2: [], subrole_n3: [] });
  const [uForm, setUForm] = useState({ email: "", password: "", name: "", first_name: "", last_name: "", role: "manager", role_level: "N2", sub_role: "bm", sectors: [] });

  useEffect(() => {
    api.get("/admin/form-options").then(({ data }) => {
      setSubRoles({
        subrole_n1: data.by_form?.subrole_n1 || [],
        subrole_n2: data.by_form?.subrole_n2 || [],
        subrole_n3: data.by_form?.subrole_n3 || [],
      });
    }).catch(() => {});
  }, []);

  const openCreate = () => { setEditing(null); setUForm({ email: "", password: "", name: "", first_name: "", last_name: "", role: "manager", role_level: "N2", sub_role: "bm", sectors: [], orbit_role: "", store_id: "" }); setModal("user"); };
  const openEdit = (u) => {
    setEditing(u);
    setUForm({
      email: u.email, password: "", name: u.name,
      first_name: u.first_name || "", last_name: u.last_name || "",
      role: u.role, role_level: u.role_level || ROLE_LEVELS[u.role] || "N2",
      sub_role: u.sub_role || "", sectors: u.sectors || [], orbit_role: u.orbit_role || "", store_id: u.store_id || ""
    });
    setModal("user");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (orbitTenant && !uForm.orbit_role) return toast.error("Choisissez un niveau et un rôle");
    if (orbitTenant && orbitRole(uForm.orbit_role)?.level === 4 && !uForm.store_id) return toast.error("Sélectionnez le magasin rattaché");
    setSaving(true);
    try {
      if (editing) {
        const payload = { name: uForm.name, first_name: uForm.first_name, last_name: uForm.last_name, role: uForm.role, role_level: uForm.role_level, sub_role: uForm.sub_role, sectors: uForm.sectors, orbit_role: uForm.orbit_role || undefined, store_id: uForm.store_id || undefined };
        if (uForm.password) payload.password = uForm.password;
        await api.patch(`/users/${editing.id}`, payload);
        await logAction("user.update", "user", { entity_id: editing.id, entity_label: uForm.name, summary: `a modifié le compte de ${uForm.name}`, before: { role: editing.role, sub_role: editing.sub_role }, after: { role: uForm.role, sub_role: uForm.sub_role } });
        toast.success("Utilisateur modifié");
      } else {
        const { data } = await api.post("/users", uForm);
        // Immediately patch to set role_level/sub_role/first_name/last_name (endpoint POST /users doesn't accept these fields)
        if (data?.id && (uForm.role_level || uForm.sub_role || uForm.first_name || uForm.last_name)) {
          await api.patch(`/users/${data.id}`, { role_level: uForm.role_level, sub_role: uForm.sub_role, first_name: uForm.first_name, last_name: uForm.last_name }).catch(() => {});
        }
        await logAction("user.create", "user", { entity_id: data.id, entity_label: uForm.name, summary: `a créé le compte ${uForm.name} (${uForm.role_level} · ${uForm.sub_role})` });
        toast.success("Utilisateur créé");
      }
      setModal(null); reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const deleteUser = async (u) => {
    if (!confirm(`Supprimer ${u.name} ?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      await logAction("user.delete", "user", { entity_id: u.id, entity_label: u.name, summary: `a supprimé le compte de ${u.name}` });
      toast.success("Supprimé"); reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const q = search.trim().toLowerCase();
  const lvlOf = (u) => orbitTenant ? String(u.orbit_level || orbitRole(u.orbit_role)?.level || (u.role === "corner" ? 4 : u.role === "admin" ? 1 : u.role === "director" ? 2 : 3)) : (u.role_level || ROLE_LEVELS[u.role] || "N2");
  const filtered = users.filter(u => {
    const lvl = lvlOf(u);
    if (levelFilter !== "all" && lvl !== levelFilter) return false;
    if (!q) return true;
    return (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q);
  });

  const levelCount = (lvl) => users.filter(u => lvl === "all" || lvlOf(u) === lvl).length;
  const availableSubRoles = subRoles[`subrole_${(uForm.role_level || "N2").toLowerCase()}`] || [];

  return (
    <div className="space-y-4" data-testid="teams-view">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-xl p-1 overflow-x-auto">
          {(orbitTenant ? [["all", "Tous", null], ["1", "N1 · Admin", Crown], ["2", "N2 · Direction", Briefcase], ["3", "N3 · Management", Briefcase], ["4", "N4 · Magasin", User]] : [["all", "Tous", null], ["N1", "N1 · Direction", Crown], ["N2", "N2 · Itinérant & Support", Briefcase], ["N3", "N3 · Corner", User]]).map(([k, l, Ic]) => (
            <button key={k} onClick={() => setLevelFilter(k)} data-testid={`teams-level-${k}`}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap flex items-center gap-1.5 ${levelFilter === k ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
              {Ic && <Ic className="w-3 h-3"/>} {l} <span className="opacity-70">({levelCount(k)})</span>
            </button>
          ))}
        </div>
        <button onClick={openCreate} data-testid="teams-add"
                className="flex items-center gap-2 px-3 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm">
          <Plus className="w-4 h-4"/> Ajouter
        </button>
      </div>

      <SearchBar value={search} onChange={setSearch} placeholder="Rechercher par nom ou email…"/>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left text-xs font-semibold tracking-wider text-slate-500 uppercase">
              <th className="px-4 py-3">Niveau</th>
              <th className="px-4 py-3">Nom</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">{orbitTenant ? "Rôle" : "Sous-rôle"}</th>
              <th className="px-4 py-3">Secteurs</th>
              <th className="px-4 py-3 w-32 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(u => {
              const lvl = lvlOf(u);
              const subLabel = orbitTenant ? (orbitRole(u.orbit_role)?.label || roleLabel[u.role] || u.role) : ((subRoles[`subrole_${lvl.toLowerCase()}`] || []).find(o => o.value === u.sub_role)?.label || (roleLabel[u.role] || u.role));
              return (
                <tr key={u.id} data-testid={`teams-user-${u.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${orbitTenant ? LEVEL_STYLE[Number(lvl)] : (LEVEL_COLOR[lvl] || "bg-slate-200")}`}>{orbitTenant ? `N${lvl}` : lvl}</span>
                  </td>
                  <td className="px-4 py-3 font-medium text-slate-900">{u.name}</td>
                  <td className="px-4 py-3 text-slate-600 text-xs">{u.email}</td>
                  <td className="px-4 py-3 text-slate-700 text-xs font-semibold">{subLabel}</td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{(u.sectors || []).map(id => sectors.find(s=>s.id===id)?.name).filter(Boolean).join(", ") || "—"}</td>
                  <td className="px-4 py-3 text-right space-x-1">
                    <button onClick={() => openEdit(u)} data-testid={`teams-edit-${u.id}`} className="w-8 h-8 grid place-items-center rounded-lg text-slate-500 hover:bg-slate-100 inline-grid">
                      <Edit3 className="w-4 h-4"/>
                    </button>
                    {u.role !== "admin" && (
                      <button onClick={() => deleteUser(u)} data-testid={`teams-delete-${u.id}`} className="w-8 h-8 grid place-items-center rounded-lg text-rose-500 hover:bg-rose-50 inline-grid">
                        <Trash2 className="w-4 h-4"/>
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="text-center py-10 text-slate-400 italic text-sm">Aucun utilisateur.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {modal === "user" && (
        <Modal onClose={() => setModal(null)} title={editing ? "Modifier l'utilisateur" : "Nouvel utilisateur"}>
          <form onSubmit={submit}>
            <div className="p-6 space-y-4 max-h-[75vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Prénom</label>
                  <input value={uForm.first_name} onChange={e=>setUForm({...uForm, first_name: e.target.value})} data-testid="teams-form-first" className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 text-sm"/>
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Nom</label>
                  <input value={uForm.last_name} onChange={e=>setUForm({...uForm, last_name: e.target.value})} data-testid="teams-form-last" className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 text-sm"/>
                </div>
              </div>
              <input required placeholder="Nom complet affiché" value={uForm.name} onChange={e=>setUForm({...uForm, name: e.target.value})} data-testid="teams-form-name" className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm"/>
              <input type="email" required placeholder="Email" value={uForm.email} disabled={!!editing} onChange={e=>setUForm({...uForm, email: e.target.value})} data-testid="teams-form-email" className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm disabled:bg-slate-50"/>
              <input type="password" minLength={6} placeholder={editing ? "Nouveau mot de passe (optionnel)" : "Mot de passe"} required={!editing} value={uForm.password} onChange={e=>setUForm({...uForm, password: e.target.value})} data-testid="teams-form-password" className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm"/>

              {orbitTenant && <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Niveau hiérarchique</label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-1">
                  {Object.entries(ORBIT_LEVELS).map(([lvl, lab]) => { const on = String(orbitRole(uForm.orbit_role)?.level || "") === lvl; return (
                    <button key={lvl} type="button" data-testid={`teams-orbit-level-${lvl}`} onClick={() => { const first = ORBIT_ROLES.find(r => r.level === Number(lvl)); setUForm({...uForm, orbit_role: first.key, role: first.role}); }}
                            className={`p-3 rounded-xl border-2 text-left transition-colors ${on ? "border-blue-600 bg-blue-50" : "border-slate-200 hover:border-slate-300"}`}>
                      <div className={`inline-flex items-center text-[10px] font-bold px-1.5 py-0.5 rounded ${LEVEL_STYLE[lvl]}`}>N{lvl}</div>
                      <div className="text-xs font-semibold text-slate-800 mt-1">{lab}</div>
                    </button>); })}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2" data-testid="teams-orbit-roles">
                  {ORBIT_ROLES.filter(r => r.level === (orbitRole(uForm.orbit_role)?.level || 0)).map(r => (
                    <button key={r.key} type="button" data-testid={`teams-orbit-role-${r.key}`} onClick={() => setUForm({...uForm, orbit_role: r.key, role: r.role})}
                            className={`px-3 py-2 rounded-lg border text-xs font-semibold text-left ${uForm.orbit_role === r.key ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}>{r.label}</button>))}
                </div>
                <div className="text-[11px] text-slate-500 mt-1">{roleHint(uForm.orbit_role) || "Choisissez un niveau puis un rôle."}</div>
                {orbitRole(uForm.orbit_role)?.level === 4 && <div className="mt-3">
                  <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Magasin rattaché</label>
                  <select data-testid="teams-form-store" value={uForm.store_id} onChange={e => setUForm({...uForm, store_id: e.target.value})} className="w-full mt-1 h-11 px-3 rounded-xl border border-slate-200 text-sm bg-white">
                    <option value="">— Choisir un magasin —</option>
                    {[...stores].sort((a, b) => (a.name || "").localeCompare(b.name || "")).map(st => <option key={st.id} value={st.id}>{st.name}{st.ville ? ` · ${st.ville}` : ""}</option>)}
                  </select>
                </div>}
              </div>}
              {!orbitTenant && <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Niveau hiérarchique</label>
                <div className="grid grid-cols-3 gap-2 mt-1">
                  {[["N1", "Direction", Crown], ["N2", "Itinérant & Support", Briefcase], ["N3", "Corner", User]].map(([lvl, lab, Ic]) => (
                    <button key={lvl} type="button" onClick={() => setUForm({...uForm, role_level: lvl, sub_role: (subRoles[`subrole_${lvl.toLowerCase()}`] || [])[0]?.value || ""})} data-testid={`teams-level-btn-${lvl}`}
                            className={`p-3 rounded-xl border-2 text-left transition-all ${uForm.role_level === lvl ? "border-blue-600 bg-blue-50" : "border-slate-200 hover:border-slate-300"}`}>
                      <div className={`inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded ${LEVEL_COLOR[lvl]}`}>
                        <Ic className="w-3 h-3"/> {lvl}
                      </div>
                      <div className="text-xs font-semibold text-slate-800 mt-1">{lab}</div>
                    </button>
                  ))}
                </div>
              </div>}

              {!orbitTenant && <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Sous-rôle</label>
                <SearchSelect value={uForm.sub_role} onChange={v => setUForm({...uForm, sub_role: v})} options={[...availableSubRoles.map(o => ({ value: o.value, label: o.label }))]} placeholder={"— Sélectionnez —"} data-testid="teams-form-subrole" size="lg" menuWidth={260}/>
                <div className="text-[11px] text-slate-500 mt-1">Modifiable dans « Configuration Formulaires » → subrole_{(uForm.role_level || "N2").toLowerCase()}.</div>
              </div>}

              {!orbitTenant && <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Rôle technique (accès)</label>
                <SearchSelect value={uForm.role} onChange={v => setUForm({...uForm, role: v})} options={[{ value: "admin", label: "Administrateur (accès total)" }, { value: "director", label: "Directeur régional" }, { value: "manager", label: "Business Manager" }, { value: "support", label: "Support" }, { value: "rt", label: "Formateur (RT)" }, { value: "mm", label: "Manager Multi-sites (MM)" }, { value: "corner", label: "Corner (accès restreint)" }]} data-testid="teams-form-role" size="lg" menuWidth={260}/>
              </div>}

              {sectors.length > 0 && (orbitTenant ? orbitRole(uForm.orbit_role)?.level !== 4 : uForm.role_level !== "N3") && (
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Secteurs rattachés</label>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {sectors.map(s => {
                      const on = uForm.sectors.includes(s.id);
                      return (
                        <button key={s.id} type="button"
                                onClick={() => setUForm(p => ({...p, sectors: on ? p.sectors.filter(x => x !== s.id) : [...p.sectors, s.id]}))}
                                className={`text-xs px-2 py-1 rounded-lg border font-semibold ${on ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
                          {s.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            <ModalFooter onCancel={() => setModal(null)} submitLabel={saving ? "…" : (editing ? "Enregistrer" : "Créer")} disabled={saving} testid="teams-form-submit"/>
          </form>
        </Modal>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  IMPORTS — centralized import tools + history
// ═══════════════════════════════════════════════════════════════════════════
function ImportsView({ reload }) {
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState({});
  const [historyLoading, setHistoryLoading] = useState(true);

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const { data } = await api.get("/admin/import-logs?limit=30");
      setHistory(data.items || []);
    } catch { /* silent */ } finally { setHistoryLoading(false); }
  };
  useEffect(() => { loadHistory(); }, []);

  const runImport = async (kind, apiPath, opts = {}) => {
    if (opts.confirmMessage && !confirm(opts.confirmMessage)) return;
    setBusy(b => ({ ...b, [kind]: true }));
    const t0 = Date.now();
    try {
      let { data } = await api.post(apiPath, opts.body || {}, opts.config || {});
      if (data?.status === "running" && data?.id?.startsWith("job_")) {
        toast.info("Import lancé en arrière-plan… traitement en cours", { id: `job-${data.id}` });
        for (;;) {
          await new Promise(r => setTimeout(r, 2000));
          const { data: j } = await api.get(`/import-jobs/${data.id}`);
          if (j.status === "done") { data = j.result || {}; break; }
          if (j.status === "error") throw { response: { data: { detail: j.error || "Échec de l'import" } } };
        }
      }
      const rows = data.counts ? Object.values(data.counts).reduce((a, b) => a + (Number(b) || 0), 0) : (data.total || data.count || data.stores || null);
      await api.post("/admin/import-logs", { kind, status: "success", rows, duration_ms: Date.now() - t0, filename: opts.filename });
      toast.success(opts.successMessage || "Import réussi");
      loadHistory(); reload?.();
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || "Échec";
      await api.post("/admin/import-logs", { kind, status: "error", duration_ms: Date.now() - t0, message: msg, filename: opts.filename }).catch(() => {});
      toast.error(msg); loadHistory();
    } finally { setBusy(b => ({ ...b, [kind]: false })); }
  };

  const uploadWeeklyBase = async (file) => {
    if (!file) return;
    if (!/\.xlsx?$/i.test(file.name)) return toast.error("Fichier Excel requis");
    const fd = new FormData(); fd.append("file", file);
    await runImport("achats", "/kpi/weekly-base/import", { body: fd, config: { timeout: 600000 }, filename: file.name, successMessage: "Achats hebdo importés (upsert)" });
  };
  const uploadHaMix = async (file) => {
    if (!file) return;
    if (!/\.xlsx?$/i.test(file.name)) return toast.error("Fichier Excel requis");
    const fd = new FormData(); fd.append("file", file);
    await runImport("mix_fournisseur", "/kpi/ha-mix/import", { body: fd, config: { timeout: 600000 }, filename: file.name, successMessage: "Mix fournisseur importé (upsert)" });
  };
  const uploadWasaplan = async (file) => {
    if (!file) return;
    if (!/\.xlsx?$/i.test(file.name)) return toast.error("Fichier Excel requis");
    const fd = new FormData(); fd.append("file", file);
    await runImport("wasaplan", "/kpi/business/import", { body: fd, config: { timeout: 600000 }, filename: file.name, successMessage: "Wasaplan importé (RAW upsert)" });
  };
  const uploadRh = async (file) => {
    if (!file) return;
    if (!/\.xlsx?$/i.test(file.name)) return toast.error("Fichier Excel requis");
    const fd = new FormData(); fd.append("file", file);
    await runImport("rh", "/kpi/rh/import", { body: fd, config: { timeout: 600000 }, filename: file.name, successMessage: "RH importé (RAW upsert)" });
  };
  const uploadProd = async (file) => {
    if (!file) return;
    if (!/\.xlsx?$/i.test(file.name)) return toast.error("Fichier Excel requis");
    const fd = new FormData(); fd.append("file", file);
    await runImport("productivite", "/kpi/prod/import", { body: fd, config: { timeout: 600000 }, filename: file.name, successMessage: "Productivité importée (RAW upsert)" });
  };
  const uploadProdTargets = async (file) => {
    if (!file) return;
    if (!/\.xlsx?$/i.test(file.name)) return toast.error("Fichier Excel requis");
    const fd = new FormData(); fd.append("file", file);
    await runImport("productivite_target", "/kpi/prod/targets/import", { body: fd, config: { timeout: 300000 }, filename: file.name, successMessage: "Objectifs Gold importés (upsert par corner)" });
  };
  const uploadQualite = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    await runImport("qualite", "/kpi/hygiene/import", { body: fd, config: { timeout: 600000 }, filename: file.name, successMessage: "Qualité importée (Eurofins + Audit Mag, RAW upsert)" });
  };
  const uploadBaseInfo = async (file) => {
    if (!file) return;
    if (!/\.xlsx?$/i.test(file.name)) return toast.error("Fichier Excel requis");
    const fd = new FormData(); fd.append("file", file);
    await runImport("base_info", "/info-mag/base-info/import", { body: fd, config: { timeout: 300000 }, filename: file.name, successMessage: "Base Info importée — fiches et magasins mis à jour" });
  };
  const uploadInventaire = async (file) => {
    if (!file) return;
    if (!/\.xlsx?$/i.test(file.name)) return toast.error("Fichier Excel requis");
    const fd = new FormData(); fd.append("file", file);
    await runImport("inventaire", "/kpi/inventaire/import", { body: fd, config: { timeout: 600000 }, filename: file.name, successMessage: "Inventaires importés (Ecart + Inventaire + Démarque)" });
  };
  const uploadXlsx = async (file) => {
    if (!file) return;
    if (!/\.xlsx?$/i.test(file.name)) return toast.error("Fichier Excel requis");
    if (!confirm(`Uploader "${file.name}" via l'import multi-onglets ?`)) return;
    const fd = new FormData(); fd.append("file", file);
    await runImport("kpi_xlsx", "/admin/upload-kpi-xlsx", { body: fd, config: { timeout: 600000 }, filename: file.name, successMessage: "Import terminé" });
  };
  const importContracts = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    await runImport("contracts", "/admin/import-contracts", { body: fd, filename: file.name, successMessage: "Contrats importés" });
  };

  const businessSlots = [
    { kind: "ca_daily", icon: TrendingUp, title: "CA Journalier", desc: "Import par période (année / mois / semaine) : seule la période choisie est mise à jour, le reste de l'historique est conservé. Historique des imports par année.", color: "bg-emerald-600", page: "/admin/imports/ca_daily", accept: ".xlsx,.xls", template: "ca_daily" },
    { kind: "achats", icon: FileSpreadsheet, title: "Achats Hebdo", desc: "Import par période (année / mois / semaine) : seule la période choisie est mise à jour, l'historique est conservé. Historique des imports par année.", color: "bg-amber-500", page: "/admin/imports/achats", accept: ".xlsx,.xls", template: "achats" },
    { kind: "mix_fournisseur", icon: FileSpreadsheet, title: "Mix Fournisseur", desc: "Import par période (année / mois / semaine) : seule la période choisie est mise à jour, l'historique est conservé. Historique des imports par année.", color: "bg-orange-600", page: "/admin/imports/mix_fournisseur", accept: ".xlsx,.xls", template: "mix_fournisseur" },
    { kind: "wasaplan", icon: FileSpreadsheet, title: "Wasaplan", desc: "Import par période (année / mois) : seule la période choisie est mise à jour, l'historique est conservé. Historique des imports par année.", color: "bg-purple-600", page: "/admin/imports/wasaplan", accept: ".xlsx,.xls", template: "wasaplan" },
    { kind: "qualite", icon: Radar, title: "Qualité (Eurofins + Audit Mag)", desc: "Import par période (année / mois) : seule la période choisie est mise à jour, l'historique est conservé. Historique des imports par année.", color: "bg-teal-600", page: "/admin/imports/qualite", accept: ".xlsx,.xls", template: "qualite" },
    { kind: "rh", icon: Users, title: "RH", desc: "Import par période (année / mois / semaine) : seule la période choisie est mise à jour, l'historique est conservé. Historique des imports par année.", color: "bg-fuchsia-600", page: "/admin/imports/rh", accept: ".xlsx,.xls", template: "rh" },
    { kind: "pl", icon: FileSpreadsheet, title: "Compte de Résultat", desc: "Import audits hygiène & qualité.", color: "bg-indigo-600", upload: true, accept: ".xlsx,.xls", onFile: uploadXlsx },
    { kind: "productivite", icon: Zap, title: "Productivité", desc: "Import par période (année / mois / semaine) : seule la période choisie est mise à jour, l'historique est conservé. Historique des imports par année.", color: "bg-blue-600", page: "/admin/imports/productivite", accept: ".xlsx,.xls", template: "productivite" },
    { kind: "productivite_target", icon: Target, title: "Productivité Target", desc: "Objectifs Gold Standard par corner (fixe, indépendant des dates). Colonnes : Corner + Gold Standard. Défaut 60 si absent.", color: "bg-yellow-500", upload: true, accept: ".xlsx,.xls", onFile: uploadProdTargets, template: "productivite_target" },
    { kind: "base_info", icon: Database, title: "Base Info", desc: "Même format que Info_corner.xlsx. Ajout / mise à jour des fiches par CORNER (aucune suppression), magasins créés ou liés automatiquement.", color: "bg-slate-700", upload: true, accept: ".xlsx,.xls", onFile: uploadBaseInfo, template: "base_info" },
    { kind: "inventaire", icon: Package, title: "Inventaires", desc: "Import par période (année / mois) : seule la période choisie est mise à jour, l'historique est conservé. Historique des imports par année.", color: "bg-cyan-600", page: "/admin/imports/inventaire", accept: ".xlsx,.xls", template: "inventaire" },
  ];

  const techTiles = [
    { kind: "alerts", icon: AlertTriangle, title: "Lancer alertes score", desc: "Envoyer les alertes aux DR/BM.", color: "bg-rose-500", action: () => runImport("alerts", "/admin/run-score-alerts") },
    { kind: "digest", icon: Mail, title: "Envoyer digest DR", desc: "Digest hebdomadaire.", color: "bg-teal-700", action: () => runImport("digest", "/admin/run-weekly-digest", { confirmMessage: "Envoyer le digest hebdo ?" }) },
    { kind: "monthly", icon: Mail, title: "Rapport mensuel PDF", desc: "Rapport PDF aux DR opt-in.", color: "bg-indigo-700", action: () => runImport("monthly", "/admin/run-monthly-report", { confirmMessage: "Envoyer le rapport mensuel ?" }) },
    { kind: "purge_achats_legacy", icon: Trash2, title: "Achats : données hors fichiers", desc: "Archive les lignes Achats Hebdo / Mix Fournisseur qui ne proviennent d'aucun import (anciens scripts : DR « Prénom NOM », doublons de corners). Rien n'est supprimé définitivement.", color: "bg-amber-700", action: () => runImport("purge_achats_legacy", "/kpi/achat/purge-legacy?apply=true", { confirmMessage: "Archiver les lignes Achats ne provenant d'aucun fichier importé ? (réversible : copies dans *_legacy_archive)", successMessage: "Lignes hors fichiers archivées — la page Achat ne lit plus que vos imports" }) },
    { kind: "ai_missions", icon: OrbitAI, title: "Suggestions missions IA", desc: "Générer des missions par IA.", color: "bg-gradient-to-r from-purple-600 to-blue-600", action: () => runImport("ai_missions", "/admin/ai-suggest-missions") },
  ];

  const renderTile = (t) => {
    const running = busy[t.kind];
    const downloadTpl = async (e) => {
      e.preventDefault();
      e.stopPropagation();
      try {
        const r = await api.get(`/kpi/import-template/${t.template}`, { responseType: "blob" });
        const url = URL.createObjectURL(r.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = `template_${t.template}.xlsx`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
        toast.success("Modèle téléchargé");
      } catch (err) {
        toast.error("Téléchargement du modèle impossible");
      }
    };
    const inner = (
      <div className={`bg-white border border-slate-200 rounded-2xl p-4 h-full flex flex-col transition-all ${running ? "opacity-60" : "hover:shadow-md hover:border-slate-300"}`}>
        <div className="flex items-start justify-between gap-2">
          <div className={`w-10 h-10 rounded-xl ${t.color} text-white grid place-items-center shadow-sm`}>
            {running ? <Loader2 className="w-5 h-5 animate-spin"/> : <t.icon className="w-5 h-5"/>}
          </div>
          {t.template && (
            <button
              type="button"
              onClick={downloadTpl}
              data-testid={`import-template-${t.kind}`}
              title="Télécharger le modèle Excel"
              className="flex items-center gap-1 text-[10px] font-semibold text-slate-600 hover:text-blue-700 hover:bg-blue-50 px-2 py-1 rounded-md border border-slate-200 hover:border-blue-300 transition-colors">
              <Download className="w-3 h-3"/> Modèle
            </button>
          )}
        </div>
        <div className="font-display font-bold text-slate-900 mt-3">{t.title}</div>
        <div className="text-xs text-slate-500 mt-1 flex-1">{t.desc}</div>
      </div>
    );
    if (t.page) {
      return (
        <Link key={t.kind} to={t.page} data-testid={`import-tile-${t.kind}`} className="block">
          {inner}
        </Link>
      );
    }
    if (t.upload) {
      return (
        <label key={t.kind} data-testid={`import-tile-${t.kind}`} className={`cursor-pointer ${running ? "pointer-events-none" : ""}`}>
          <input type="file" accept={t.accept} hidden onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; t.onFile(f, t.kind); }}/>
          {inner}
        </label>
      );
    }
    return (
      <button key={t.kind} onClick={t.action} disabled={running} data-testid={`import-tile-${t.kind}`} className="text-left disabled:cursor-not-allowed">
        {inner}
      </button>
    );
  };

  return (
    <div className="space-y-6" data-testid="imports-view">
      <AdminDocuments/>
      {/* Business slots — 6 métier */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-6 h-6 rounded-lg bg-emerald-500 text-white grid place-items-center"><TrendingUp className="w-3.5 h-3.5"/></div>
          <h3 className="font-display font-bold text-slate-900">Slots métier</h3>
          <span className="text-xs text-slate-500">— glissez un fichier Excel dans la tuile correspondante</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {businessSlots.map(renderTile)}
        </div>
      </div>

      {/* Produits & Mix vente */}
      <ProduitsBlocks onImported={loadHistory} />
      <QualityImportModels />

      {/* Tech actions */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-6 h-6 rounded-lg bg-slate-500 text-white grid place-items-center"><Layers className="w-3.5 h-3.5"/></div>
          <h3 className="font-display font-bold text-slate-900">Actions techniques</h3>
          <span className="text-xs text-slate-500">— consolidation, seeds, envois d'emails, IA</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {techTiles.map(renderTile)}
        </div>
      </div>

      {/* Import history */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ScrollText className="w-4 h-4 text-slate-500"/>
            <h3 className="font-display font-bold text-slate-900">Historique des imports</h3>
          </div>
          <button onClick={loadHistory} className="text-xs font-bold text-blue-600 hover:text-blue-800 flex items-center gap-1"><RefreshCw className={`w-3 h-3 ${historyLoading ? "animate-spin" : ""}`}/> Actualiser</button>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left text-xs font-semibold tracking-wider text-slate-500 uppercase">
              <th className="px-4 py-2">Type</th>
              <th className="px-4 py-2">Fichier</th>
              <th className="px-4 py-2">Par</th>
              <th className="px-4 py-2">Date</th>
              <th className="px-4 py-2">Lignes</th>
              <th className="px-4 py-2">Durée</th>
              <th className="px-4 py-2">Statut</th>
            </tr>
          </thead>
          <tbody>
            {history.map(h => (
              <tr key={h.id} data-testid={`import-history-${h.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-2 font-mono text-xs text-slate-700">{h.kind}</td>
                <td className="px-4 py-2 text-xs text-slate-500">{h.filename || "—"}</td>
                <td className="px-4 py-2 text-xs text-slate-600">{h.user_name || "—"}</td>
                <td className="px-4 py-2 text-xs text-slate-500">{new Date(h.created_at).toLocaleString("fr-FR")}</td>
                <td className="px-4 py-2 text-xs font-bold text-slate-700">{h.rows ?? "—"}</td>
                <td className="px-4 py-2 text-xs text-slate-500">{h.duration_ms != null ? `${(h.duration_ms/1000).toFixed(1)}s` : "—"}</td>
                <td className="px-4 py-2">
                  {h.status === "success"
                    ? <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full"><Check className="w-3 h-3"/>Succès</span>
                    : <span className="inline-flex items-center gap-1 text-[11px] font-bold text-rose-700 bg-rose-50 border border-rose-200 px-2 py-0.5 rounded-full"><X className="w-3 h-3"/>Échec</span>}
                </td>
              </tr>
            ))}
            {history.length === 0 && !historyLoading && (
              <tr><td colSpan={7} className="text-center py-10 text-slate-400 italic text-sm">Aucun import enregistré pour l'instant.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  FORMS — configure dropdown options
// ═══════════════════════════════════════════════════════════════════════════
const FORM_LABELS = {
  issue_category: { label: "Catégories de Problématiques", icon: AlertTriangle, color: "bg-rose-500" },
  mission_type: { label: "Types de Missions", icon: Radar, color: "bg-indigo-500" },
  rh_poste: { label: "Postes RH (recrutement)", icon: Users, color: "bg-purple-500" },
  audit_type: { label: "Types d'Audit", icon: ListChecks, color: "bg-emerald-500" },
  enseigne: { label: "Enseignes magasins", icon: Store, color: "bg-blue-500" },
  subrole_n1: { label: "Sous-rôles N1 (Direction)", icon: Crown, color: "bg-rose-600" },
  subrole_n2: { label: "Sous-rôles N2 (Itinérant & Support)", icon: Briefcase, color: "bg-indigo-600" },
  subrole_n3: { label: "Sous-rôles N3 (Corner)", icon: User, color: "bg-slate-500" },
};

function FormsView() {
  const [tab, setTab] = useState("options");
  return (
    <div className="space-y-4" data-testid="forms-view">
      <div className="flex items-center gap-1 bg-slate-100 rounded-xl p-1 w-fit">
        <button onClick={() => setTab("options")} data-testid="forms-tab-options"
                className={`px-4 py-2 rounded-lg text-xs font-bold flex items-center gap-1.5 ${tab === "options" ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}>
          <ListChecks className="w-3.5 h-3.5"/> Listes déroulantes
        </button>
        <button onClick={() => setTab("builder")} data-testid="forms-tab-builder"
                className={`px-4 py-2 rounded-lg text-xs font-bold flex items-center gap-1.5 ${tab === "builder" ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}>
          <PenSquare className="w-3.5 h-3.5"/> Builder de formulaires
        </button>
      </div>
      {tab === "options" && <FormsOptionsView/>}
      {tab === "builder" && <AdminFormBuilder/>}
    </div>
  );
}

function FormsOptionsView() {
  const [byForm, setByForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeForm, setActiveForm] = useState("issue_category");
  const [modal, setModal] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ value: "", label: "", color: "#0ea5e9", active: true });
  const [newFormKey, setNewFormKey] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/form-options");
      setByForm(data.by_form || {});
    } catch { toast.error("Chargement impossible"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm({ value: "", label: "", color: "#0ea5e9", active: true }); setModal("option"); };
  const openEdit = (o) => { setEditing(o); setForm({ value: o.value, label: o.label, color: o.color || "#0ea5e9", active: o.active !== false }); setModal("option"); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.patch(`/admin/form-options/${editing.id}`, form);
        toast.success("Option modifiée");
      } else {
        await api.post("/admin/form-options", { ...form, form_key: activeForm });
        toast.success("Option ajoutée");
      }
      setModal(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (o) => {
    if (!confirm(`Supprimer « ${o.label} » ?`)) return;
    try { await api.delete(`/admin/form-options/${o.id}`); toast.success("Supprimé"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const createForm = async (e) => {
    e.preventDefault();
    const key = newFormKey.trim().toLowerCase().replace(/[^a-z0-9_]/g, "_");
    if (!key) return;
    try {
      await api.post("/admin/form-options/form", { form_key: key });
      toast.success(`Formulaire ${key} créé`);
      setActiveForm(key); setNewFormKey(""); setModal(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const allFormKeys = Array.from(new Set([...Object.keys(FORM_LABELS), ...Object.keys(byForm)]));
  const options = byForm[activeForm] || [];

  return (
    <div className="space-y-4" data-testid="forms-view">
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Left column — forms list */}
        <div className="lg:col-span-1 space-y-2">
          <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500 px-1">Formulaires</div>
          {allFormKeys.map(k => {
            const cfg = FORM_LABELS[k] || { label: k, icon: PenSquare, color: "bg-slate-500" };
            const count = (byForm[k] || []).length;
            const on = activeForm === k;
            const Icon = cfg.icon;
            return (
              <button key={k} onClick={() => setActiveForm(k)} data-testid={`forms-list-${k}`}
                      className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all ${on ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 hover:border-slate-300"}`}>
                <div className={`w-8 h-8 rounded-lg ${cfg.color} text-white grid place-items-center shrink-0`}>
                  <Icon className="w-4 h-4"/>
                </div>
                <div className="text-left flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate">{cfg.label}</div>
                  <div className={`text-[11px] ${on ? "text-slate-300" : "text-slate-500"}`}>{count} option{count > 1 ? "s" : ""}</div>
                </div>
              </button>
            );
          })}
          <button onClick={() => setModal("form")} data-testid="forms-add-form"
                  className="w-full flex items-center gap-2 p-3 rounded-xl border border-dashed border-slate-300 hover:border-blue-400 hover:bg-blue-50 text-slate-500 hover:text-blue-700 text-sm font-semibold">
            <Plus className="w-4 h-4"/> Nouveau formulaire
          </button>
        </div>

        {/* Right column — options table */}
        <div className="lg:col-span-3">
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
              <div>
                <div className="font-display font-bold text-slate-900">{FORM_LABELS[activeForm]?.label || activeForm}</div>
                <div className="text-xs text-slate-500">Clé technique : <code className="bg-slate-100 px-1.5 py-0.5 rounded text-slate-700 font-mono">{activeForm}</code></div>
              </div>
              <button onClick={openCreate} data-testid="forms-add-option"
                      className="flex items-center gap-2 px-3 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm">
                <Plus className="w-4 h-4"/> Ajouter une option
              </button>
            </div>
            {loading ? (
              <div className="py-10 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline"/></div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr className="text-left text-xs font-semibold tracking-wider text-slate-500 uppercase">
                    <th className="px-4 py-2 w-10"></th>
                    <th className="px-4 py-2">Libellé</th>
                    <th className="px-4 py-2">Valeur (code)</th>
                    <th className="px-4 py-2">Statut</th>
                    <th className="px-4 py-2 w-24 text-right"></th>
                  </tr>
                </thead>
                <tbody>
                  {options.map(o => (
                    <tr key={o.id} data-testid={`forms-option-${o.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-4 py-2">
                        <span className="inline-block w-4 h-4 rounded-full border border-slate-200" style={{ backgroundColor: o.color || "#94a3b8" }}/>
                      </td>
                      <td className="px-4 py-2 font-medium text-slate-900">{o.label}</td>
                      <td className="px-4 py-2 text-xs font-mono text-slate-500">{o.value}</td>
                      <td className="px-4 py-2">
                        {o.active !== false
                          ? <span className="text-[11px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">Actif</span>
                          : <span className="text-[11px] font-bold text-slate-500 bg-slate-100 border border-slate-200 px-2 py-0.5 rounded-full">Masqué</span>}
                      </td>
                      <td className="px-4 py-2 text-right space-x-1">
                        <button onClick={() => openEdit(o)} data-testid={`forms-edit-${o.id}`} className="w-8 h-8 grid place-items-center rounded-lg text-slate-500 hover:bg-slate-100 inline-grid"><Edit3 className="w-4 h-4"/></button>
                        <button onClick={() => del(o)} data-testid={`forms-delete-${o.id}`} className="w-8 h-8 grid place-items-center rounded-lg text-rose-500 hover:bg-rose-50 inline-grid"><Trash2 className="w-4 h-4"/></button>
                      </td>
                    </tr>
                  ))}
                  {options.length === 0 && (
                    <tr><td colSpan={5} className="text-center py-10 text-slate-400 italic text-sm">Aucune option — ajoutez-en une !</td></tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {modal === "option" && (
        <Modal onClose={() => setModal(null)} title={editing ? "Modifier l'option" : `Ajouter à ${FORM_LABELS[activeForm]?.label || activeForm}`}>
          <form onSubmit={submit}>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Libellé (affiché)</label>
                <input required value={form.label} onChange={e=>setForm({...form, label: e.target.value})} data-testid="forms-modal-label" className="w-full mt-1 px-3 py-2.5 rounded-xl border border-slate-200 text-sm" placeholder="Ex: Hygiène"/>
              </div>
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Valeur technique (code)</label>
                <input required value={form.value} onChange={e=>setForm({...form, value: e.target.value})} data-testid="forms-modal-value" className="w-full mt-1 px-3 py-2.5 rounded-xl border border-slate-200 text-sm font-mono" placeholder="Ex: hygiene"/>
              </div>
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Couleur</label>
                <div className="flex items-center gap-2 mt-1">
                  <input type="color" value={form.color} onChange={e=>setForm({...form, color: e.target.value})} data-testid="forms-modal-color" className="w-16 h-10 rounded-lg border border-slate-200 cursor-pointer"/>
                  <input type="text" value={form.color} onChange={e=>setForm({...form, color: e.target.value})} className="flex-1 px-3 py-2 rounded-xl border border-slate-200 text-sm font-mono"/>
                </div>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.active} onChange={e=>setForm({...form, active: e.target.checked})} className="w-4 h-4 rounded"/>
                <span className="text-sm text-slate-700">Option active (visible dans les formulaires)</span>
              </label>
            </div>
            <ModalFooter onCancel={() => setModal(null)} submitLabel={editing ? "Enregistrer" : "Ajouter"} testid="forms-modal-submit"/>
          </form>
        </Modal>
      )}

      {modal === "form" && (
        <Modal onClose={() => setModal(null)} title="Nouveau formulaire">
          <form onSubmit={createForm}>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Clé technique</label>
                <input required value={newFormKey} onChange={e=>setNewFormKey(e.target.value)} data-testid="forms-new-form-key" className="w-full mt-1 px-3 py-2.5 rounded-xl border border-slate-200 text-sm font-mono" placeholder="ex: audit_priority"/>
                <div className="text-[11px] text-slate-500 mt-1">Uniquement lettres minuscules, chiffres et underscore.</div>
              </div>
            </div>
            <ModalFooter onCancel={() => setModal(null)} submitLabel="Créer"/>
          </form>
        </Modal>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  AUDIT — full audit log
// ═══════════════════════════════════════════════════════════════════════════
function AuditView() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [entityType, setEntityType] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "150" });
      if (q) params.set("q", q);
      if (entityType !== "all") params.set("entity_type", entityType);
      const { data } = await api.get(`/admin/audit-log?${params}`);
      setItems(data.items || []); setTotal(data.total || 0);
    } catch { toast.error("Chargement impossible"); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [entityType]);

  const entities = ["all", "user", "store", "sector", "mapping", "import", "form_option"];
  const entityLabel = { all: "Tous", user: "Utilisateurs", store: "Magasins", sector: "Secteurs", mapping: "Mapping", import: "Imports", form_option: "Formulaires" };

  return (
    <div className="space-y-4" data-testid="audit-view">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-xl p-1 overflow-x-auto">
          {entities.map(e => (
            <button key={e} onClick={() => setEntityType(e)} data-testid={`audit-filter-${e}`}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap ${entityType === e ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
              {entityLabel[e]}
            </button>
          ))}
        </div>
        <div className="flex-1 min-w-[240px]">
          <SearchBar value={q} onChange={setQ} placeholder="Rechercher (nom, action, cible)…" onEnter={load}/>
        </div>
        <button onClick={load} data-testid="audit-refresh"
                className="px-3 py-2 rounded-xl bg-white border border-slate-200 hover:bg-slate-50 text-xs font-semibold flex items-center gap-1">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`}/> Actualiser
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between text-xs text-slate-500">
          <span><b className="text-slate-900">{total}</b> événement{total > 1 ? "s" : ""} au total</span>
          {items.length < total && <span>{items.length} affichés</span>}
        </div>
        <ul className="divide-y divide-slate-100">
          {items.map(a => (
            <li key={a.id} data-testid={`audit-item-${a.id}`} className="px-5 py-3 flex items-start gap-3 hover:bg-slate-50">
              <div className="w-9 h-9 rounded-xl bg-slate-100 text-slate-500 grid place-items-center shrink-0">
                <Activity className="w-4 h-4"/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-slate-800">
                  <b>{a.user_name}</b>{" "}
                  {a.summary || <><span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">{a.action}</span> {a.entity_label}</>}
                </div>
                <div className="flex items-center gap-2 mt-0.5 text-[11px] text-slate-400">
                  <span>{new Date(a.created_at).toLocaleString("fr-FR")}</span>
                  <span>·</span>
                  <span className="font-mono">{a.action}</span>
                  {a.entity_type && <><span>·</span><span className="font-semibold text-slate-500">{entityLabel[a.entity_type] || a.entity_type}</span></>}
                </div>
              </div>
            </li>
          ))}
          {items.length === 0 && !loading && (
            <li className="text-center py-14 text-slate-400 italic text-sm">Aucun événement enregistré{q ? ` pour « ${q} »` : ""}.</li>
          )}
          {loading && <li className="text-center py-10 text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline"/></li>}
        </ul>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  CORNER MAPPING — legacy tab kept inside Parc
// ═══════════════════════════════════════════════════════════════════════════
function CornerMappingTab({ stores }) {
  const [orphans, setOrphans] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [aliases, setAliases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(null);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [view, setView] = useState("orphans");  // orphans | mapped | aliases
  const [picks, setPicks] = useState({});
  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [search, setSearch] = useState("");
  const [aliasModal, setAliasModal] = useState(null);   // { canonical, store_id, aliases: [] }

  const load = async () => {
    setLoading(true);
    try {
      const [o, m, a] = await Promise.all([
        api.get("/admin/corner-mapping/orphans"),
        api.get("/admin/corner-mapping"),
        api.get("/admin/corner-aliases"),
      ]);
      setOrphans(o.data.orphans || []); setMappings(m.data.mappings || []); setAliases(a.data.items || []);
    } catch { toast.error("Chargement impossible"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openNewAlias = (fromOrphan) => {
    if (fromOrphan) setAliasModal({ canonical: fromOrphan, store_id: "", aliases: [fromOrphan], aliasInput: "" });
    else setAliasModal({ canonical: "", store_id: "", aliases: [], aliasInput: "" });
  };

  const saveAlias = async () => {
    if (!aliasModal.canonical.trim() || aliasModal.aliases.length === 0) return toast.error("Renseignez au moins le nom canonique et 1 alias");
    try {
      const { data } = await api.post("/admin/corner-aliases", {
        canonical: aliasModal.canonical, store_id: aliasModal.store_id || null, aliases: aliasModal.aliases,
      });
      await logAction("alias.create", "mapping", { entity_label: aliasModal.canonical,
        summary: `a créé une correspondance multi-noms « ${aliasModal.canonical} » (${aliasModal.aliases.length} alias · ${data.applied} lignes CA rattachées)` });
      toast.success(`Correspondance créée · ${data.applied} lignes CA rattachées`);
      setAliasModal(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const deleteAlias = async (a) => {
    if (!confirm(`Supprimer la correspondance « ${a.canonical} » ?`)) return;
    try { await api.delete(`/admin/corner-aliases/${a.id}`); toast.success("Supprimé"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveMapping = async (ck, sid) => {
    if (!sid) return toast.error("Sélectionnez un magasin");
    setSaving(ck);
    try {
      const { data } = await api.post("/admin/corner-mapping", { corner_key: ck, store_id: sid });
      await logAction("mapping.create", "mapping", { entity_id: ck, entity_label: ck, summary: `a mappé le corner « ${ck} » (${data.updated_rows} lignes CA rattachées)` });
      toast.success(`Mappé (${data.updated_rows} lignes CA mises à jour)`);
      setPicks(p => { const n = {...p}; delete n[ck]; return n; });
      setSelectedKeys(prev => { const n = new Set(prev); n.delete(ck); return n; });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSaving(null);
  };

  const bulkSave = async () => {
    const readyMappings = [...selectedKeys].map(ck => ({ corner_key: ck, store_id: picks[ck] })).filter(m => m.store_id);
    if (readyMappings.length === 0) return toast.error("Sélectionnez au moins un corner avec un magasin choisi");
    if (!confirm(`Enregistrer ${readyMappings.length} mapping(s) ?`)) return;
    setBulkSaving(true);
    try {
      const { data } = await api.post("/admin/corner-mapping/bulk", { mappings: readyMappings });
      await logAction("mapping.bulk", "mapping", { summary: `a effectué un mapping bulk : ${data.saved} corners liés · ${data.updated_ca_rows} lignes CA` });
      toast.success(`${data.saved} mapping(s) enregistré(s) · ${data.updated_ca_rows} lignes CA mises à jour`);
      setPicks({}); setSelectedKeys(new Set()); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBulkSaving(false);
  };

  const bulkAutoSuggest = () => {
    const next = { ...picks };
    [...selectedKeys].forEach(ck => {
      const o = orphans.find(x => x.corner_key === ck);
      if (o && o.suggestions && o.suggestions.length > 0 && !next[ck]) next[ck] = o.suggestions[0].store_id;
    });
    setPicks(next);
    toast.success("Meilleures suggestions appliquées — vérifiez avant d'enregistrer");
  };

  const removeMapping = async (ck) => {
    if (!confirm("Supprimer ce mapping ?")) return;
    setSaving(ck);
    try {
      await api.delete(`/admin/corner-mapping/${encodeURIComponent(ck)}`);
      await logAction("mapping.delete", "mapping", { entity_id: ck, entity_label: ck, summary: `a supprimé le mapping « ${ck} »` });
      toast.success("Mapping supprimé"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSaving(null);
  };

  const q = search.trim().toLowerCase();
  const filteredOrphans = orphans.filter(o => !q || (o.corner || "").toLowerCase().includes(q) || (o.secteur || "").toLowerCase().includes(q) || (o.bm || "").toLowerCase().includes(q));
  const filteredMappings = mappings.filter(m => !q || (m.corner_key || "").toLowerCase().includes(q) || (m.store_name || "").toLowerCase().includes(q));

  return (
    <div className="space-y-4">
      <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="font-display text-lg font-bold text-slate-900 flex items-center gap-2">
              <Link2 className="w-5 h-5 text-amber-600"/> Mapping manuel des corners CA
            </h2>
            <p className="text-xs text-slate-600 mt-0.5">Relier les lignes Excel journalières à leurs magasins Orbit.</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="text-[10px] font-bold uppercase text-slate-500">Orphelins</div>
              <div className="font-display text-2xl font-bold text-amber-700">{orphans.length}</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] font-bold uppercase text-slate-500">Mappés</div>
              <div className="font-display text-2xl font-bold text-emerald-700">{mappings.length}</div>
            </div>
            <button onClick={load} disabled={loading} className="px-3 py-2 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 text-xs font-semibold flex items-center gap-1">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`}/> Actualiser
            </button>
          </div>
        </div>
      </div>

      <SearchBar value={search} onChange={setSearch} placeholder="Rechercher un corner, secteur, BM…"/>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-1 bg-slate-100 rounded-xl p-1 w-fit">
          <button onClick={() => setView("orphans")}
                  className={`px-4 py-2 rounded-lg text-xs font-bold ${view === "orphans" ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}>
            À mapper ({filteredOrphans.length})
          </button>
          <button onClick={() => setView("mapped")}
                  className={`px-4 py-2 rounded-lg text-xs font-bold ${view === "mapped" ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}>
            Déjà mappés ({filteredMappings.length})
          </button>
          <button onClick={() => setView("aliases")} data-testid="mapping-view-aliases"
                  className={`px-4 py-2 rounded-lg text-xs font-bold ${view === "aliases" ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}>
            Correspondances ({aliases.length})
          </button>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => openNewAlias(null)} data-testid="mapping-new-alias"
                  className="px-3 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold flex items-center gap-1">
            <Layers3 className="w-3.5 h-3.5"/> Créer correspondance
          </button>
        </div>
        {view === "orphans" && selectedKeys.size > 0 && (
          <div className="flex items-center gap-2 bg-indigo-50 border border-indigo-200 rounded-xl px-3 py-2">
            <span className="text-xs font-bold text-indigo-900">{selectedKeys.size} sélectionné{selectedKeys.size > 1 ? "s" : ""}</span>
            <button onClick={bulkAutoSuggest} className="px-3 py-1.5 rounded-lg bg-white border border-indigo-200 hover:bg-indigo-100 text-indigo-700 text-xs font-bold flex items-center gap-1">
              <Wand2 className="w-3 h-3"/> Auto-suggérer
            </button>
            <button onClick={bulkSave} disabled={bulkSaving} data-testid="bulk-map-selected"
                    className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold flex items-center gap-1 disabled:opacity-40">
              {bulkSaving ? <Loader2 className="w-3 h-3 animate-spin"/> : <Check className="w-3 h-3"/>}
              Mapper la sélection ({[...selectedKeys].filter(ck => picks[ck]).length}/{selectedKeys.size})
            </button>
            <button onClick={() => { setSelectedKeys(new Set()); setPicks({}); }} className="px-2 py-1.5 rounded-lg text-xs font-semibold text-slate-500 hover:text-slate-900">Désélectionner</button>
          </div>
        )}
      </div>

      {loading && <div className="text-center py-8 text-slate-400 text-sm"><Loader2 className="w-4 h-4 animate-spin inline mr-1"/>Chargement…</div>}

      {!loading && view === "orphans" && (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-[10px] font-bold tracking-wider text-slate-500 uppercase">
                <th className="px-2 py-2 w-8">
                  <input type="checkbox" checked={filteredOrphans.length > 0 && filteredOrphans.slice(0, 300).every(o => selectedKeys.has(o.corner_key))}
                         onChange={(e) => { if (e.target.checked) setSelectedKeys(new Set(filteredOrphans.slice(0, 300).map(o => o.corner_key))); else setSelectedKeys(new Set()); }}
                         className="w-3.5 h-3.5"/>
                </th>
                <th className="px-3 py-2">Corner Excel</th>
                <th className="px-3 py-2">Secteur · BM</th>
                <th className="px-3 py-2 text-right">Lignes</th>
                <th className="px-3 py-2">Suggestions</th>
                <th className="px-3 py-2 min-w-[240px]">Magasin</th>
                <th className="px-3 py-2 text-right"></th>
              </tr>
            </thead>
            <tbody>
              {filteredOrphans.slice(0, 300).map(o => {
                const picked = picks[o.corner_key] || "";
                const isSelected = selectedKeys.has(o.corner_key);
                return (
                  <tr key={o.corner_key} className={`border-t border-slate-100 ${isSelected ? "bg-indigo-50/40" : "hover:bg-slate-50/50"}`}>
                    <td className="px-2 py-2 w-8">
                      <input type="checkbox" checked={isSelected}
                             onChange={(e) => setSelectedKeys(prev => { const n = new Set(prev); if (e.target.checked) n.add(o.corner_key); else n.delete(o.corner_key); return n; })}
                             className="w-3.5 h-3.5"/>
                    </td>
                    <td className="px-3 py-2 font-semibold text-slate-900">{o.corner}</td>
                    <td className="px-3 py-2 text-slate-500">
                      <div>{o.secteur || "—"}</div>
                      <div className="text-[10px] text-slate-400">{o.bm || "—"} · {o.dr || "—"}</div>
                    </td>
                    <td className="px-3 py-2 text-right font-bold text-amber-700">{o.count}</td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-1">
                        {(o.suggestions || []).map(s => (
                          <button key={s.store_id} onClick={() => setPicks(p => ({...p, [o.corner_key]: s.store_id}))}
                                  className={`text-[10px] px-1.5 py-0.5 rounded font-semibold border ${picked === s.store_id ? "bg-blue-600 text-white border-blue-600" : "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100"}`}>
                            {s.label} <span className="opacity-70">{s.score}%</span>
                          </button>
                        ))}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <SearchSelect value={picked} onChange={v => setPicks(p => ({...p, [o.corner_key]: v}))} options={[...stores.slice().sort((a,b) => (a.name||"").localeCompare(b.name||"")).map(s => ({ value: s.id, label: `${s.name}${s.ville ? ` · ${s.ville}` : ""}` }))]} placeholder={"— Choisir un magasin —"} size="lg" menuWidth={260}/>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => saveMapping(o.corner_key, picked)} disabled={!picked || saving === o.corner_key}
                              className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold disabled:opacity-40 flex items-center gap-1">
                        {saving === o.corner_key ? <Loader2 className="w-3 h-3 animate-spin"/> : <Check className="w-3 h-3"/>} Mapper
                      </button>
                    </td>
                  </tr>
                );
              })}
              {filteredOrphans.length === 0 && (
                <tr><td colSpan={7} className="text-center py-10 text-slate-400 italic text-sm">
                  {orphans.length === 0 ? "🎉 Tous les corners sont mappés !" : `Aucun orphelin ne correspond à « ${search} »`}
                </td></tr>
              )}
            </tbody>
          </table>
          {filteredOrphans.length > 300 && (
            <div className="text-center py-3 text-[10px] text-slate-400 border-t border-slate-100">
              Affichage limité aux 300 premiers · utilisez la recherche pour cibler
            </div>
          )}
        </div>
      )}

      {!loading && view === "mapped" && (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-[10px] font-bold tracking-wider text-slate-500 uppercase">
                <th className="px-3 py-2">Corner (clé)</th>
                <th className="px-3 py-2">Magasin lié</th>
                <th className="px-3 py-2">Créé</th>
                <th className="px-3 py-2 text-right w-24"></th>
              </tr>
            </thead>
            <tbody>
              {filteredMappings.map(m => (
                <tr key={m.corner_key} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2 font-semibold text-slate-900">{m.corner_key}</td>
                  <td className="px-3 py-2 text-slate-700">{m.store_name || m.store_id}</td>
                  <td className="px-3 py-2 text-slate-500">{m.created_at ? new Date(m.created_at).toLocaleDateString("fr-FR") : "—"}</td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => removeMapping(m.corner_key)} disabled={saving === m.corner_key}
                            className="px-2 py-1 rounded-lg bg-rose-50 text-rose-700 hover:bg-rose-100 text-[11px] font-bold">Retirer</button>
                  </td>
                </tr>
              ))}
              {filteredMappings.length === 0 && (
                <tr><td colSpan={4} className="text-center py-10 text-slate-400 italic text-sm">Aucun mapping.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {!loading && view === "aliases" && (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="aliases-list">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-[10px] font-bold tracking-wider text-slate-500 uppercase">
                <th className="px-3 py-2">Corner canonique</th>
                <th className="px-3 py-2">Magasin lié</th>
                <th className="px-3 py-2">Alias enregistrés</th>
                <th className="px-3 py-2">Créé</th>
                <th className="px-3 py-2 text-right w-24"></th>
              </tr>
            </thead>
            <tbody>
              {aliases.map(a => (
                <tr key={a.id} data-testid={`alias-${a.id}`} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2 font-semibold text-slate-900">{a.canonical}</td>
                  <td className="px-3 py-2 text-slate-700">{stores.find(s => s.id === a.store_id)?.name || <span className="text-slate-400 italic">non lié</span>}</td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {(a.aliases || []).map((al, i) => (
                        <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200 font-semibold">{al}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-slate-500">{a.created_at ? new Date(a.created_at).toLocaleDateString("fr-FR") : "—"}</td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => deleteAlias(a)} className="px-2 py-1 rounded-lg bg-rose-50 text-rose-700 hover:bg-rose-100 text-[11px] font-bold">Supprimer</button>
                  </td>
                </tr>
              ))}
              {aliases.length === 0 && (
                <tr><td colSpan={5} className="text-center py-10 text-slate-400 italic text-sm">Aucune correspondance multi-noms encore. Cliquez sur <b>Créer correspondance</b>.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {aliasModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm grid place-items-center p-4" onClick={() => setAliasModal(null)}>
          <form onClick={e => e.stopPropagation()} onSubmit={e => { e.preventDefault(); saveAlias(); }}
                className="bg-white rounded-2xl w-full max-w-lg border border-slate-200 shadow-xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <div>
                <h2 className="font-display text-xl font-semibold">Créer une correspondance multi-noms</h2>
                <p className="text-xs text-slate-500 mt-0.5">Regroupez plusieurs orthographes/exports vers un seul corner.</p>
              </div>
              <button type="button" onClick={() => setAliasModal(null)} className="w-8 h-8 grid place-items-center rounded-lg hover:bg-slate-100"><X className="w-4 h-4"/></button>
            </div>
            <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Nom canonique</label>
                <input required value={aliasModal.canonical} onChange={e => setAliasModal({...aliasModal, canonical: e.target.value})} data-testid="alias-canonical"
                       className="w-full mt-1 px-3 py-2.5 rounded-xl border border-slate-200 text-sm" placeholder="Ex: Leclerc Cannes La Bocca"/>
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Magasin lié (optionnel)</label>
                <SearchSelect value={aliasModal.store_id} onChange={v => setAliasModal({...aliasModal, store_id: v})} options={[...stores.slice().sort((a,b) => (a.name||"").localeCompare(b.name||"")).map(s => ({ value: s.id, label: `${s.name}${s.ville ? ` · ${s.ville}` : ""}` }))]} placeholder={"— (à mapper plus tard) —"} data-testid="alias-store" size="lg" menuWidth={260}/>
                <div className="text-[11px] text-slate-500 mt-1">Si un magasin est choisi, toutes les lignes CA orphelines correspondant aux alias seront rattachées automatiquement.</div>
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Alias (variantes d'orthographe)</label>
                <div className="flex flex-wrap gap-1.5 mt-1 p-2 border border-slate-200 rounded-xl bg-slate-50 min-h-[48px]">
                  {aliasModal.aliases.map((a, i) => (
                    <span key={i} className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-blue-100 text-blue-800 text-xs font-semibold">
                      {a}
                      <button type="button" onClick={() => setAliasModal({...aliasModal, aliases: aliasModal.aliases.filter((_, j) => j !== i)})}
                              className="w-4 h-4 grid place-items-center rounded-full hover:bg-blue-200"><X className="w-3 h-3"/></button>
                    </span>
                  ))}
                  <input value={aliasModal.aliasInput || ""} data-testid="alias-input"
                         onChange={e => setAliasModal({...aliasModal, aliasInput: e.target.value})}
                         onKeyDown={e => {
                           if ((e.key === "Enter" || e.key === ",") && aliasModal.aliasInput?.trim()) {
                             e.preventDefault();
                             const v = aliasModal.aliasInput.trim();
                             if (!aliasModal.aliases.includes(v)) setAliasModal({...aliasModal, aliases: [...aliasModal.aliases, v], aliasInput: ""});
                             else setAliasModal({...aliasModal, aliasInput: ""});
                           }
                         }}
                         className="flex-1 min-w-[160px] bg-transparent outline-none text-sm px-2" placeholder="Tapez un alias puis Entrée"/>
                </div>
                <div className="text-[11px] text-slate-500 mt-1">Chaque alias sera reconnu lors des futurs imports Excel. Ajoutez-en autant que nécessaire.</div>
              </div>
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-100 bg-slate-50 rounded-b-2xl">
              <button type="button" onClick={() => setAliasModal(null)} className="px-4 py-2 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-100">Annuler</button>
              <button type="submit" data-testid="alias-submit" className="px-4 py-2 rounded-xl text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white">Enregistrer</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  SHARED PRIMITIVES
// ═══════════════════════════════════════════════════════════════════════════
function SearchBar({ value, onChange, placeholder, onEnter }) {
  return (
    <div className="relative">
      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
      <input value={value} onChange={e => onChange(e.target.value)}
             onKeyDown={e => { if (e.key === "Enter" && onEnter) onEnter(); }}
             placeholder={placeholder}
             className="w-full pl-10 pr-10 py-2.5 rounded-xl border border-slate-200 bg-white text-sm outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400"/>
      {value && (
        <button onClick={() => onChange("")} className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 grid place-items-center text-slate-400 hover:text-slate-700 rounded-lg hover:bg-slate-100">
          <X className="w-3.5 h-3.5"/>
        </button>
      )}
    </div>
  );
}

function Modal({ children, onClose, title }) {
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-md border border-slate-200 shadow-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h2 className="font-display text-xl font-semibold">{title}</h2>
          <button type="button" onClick={onClose} className="w-8 h-8 grid place-items-center rounded-lg hover:bg-slate-100"><X className="w-4 h-4"/></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ModalFooter({ onCancel, submitLabel, disabled, testid }) {
  return (
    <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-100 bg-slate-50 rounded-b-2xl">
      <button type="button" onClick={onCancel} className="px-4 py-2 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-100">Annuler</button>
      <button type="submit" disabled={disabled} data-testid={testid} className="px-4 py-2 rounded-xl text-sm font-semibold bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-60">{submitLabel}</button>
    </div>
  );
}

